import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { applicationApi, type Application, type ApiError } from '../lib/api'

/**
 * Hook for fetching an application by lead ID.
 */
export function useApplication(leadId: string | undefined) {
  return useQuery({
    queryKey: ['application', leadId],
    queryFn: () => applicationApi.get(leadId!),
    enabled: !!leadId,
    retry: (failureCount, error) => {
      // Don't retry 404s (application doesn't exist yet)
      if ((error as ApiError)?.response?.status === 404) {
        return false
      }
      return failureCount < 3
    },
  })
}

/**
 * Hook for creating a new application for a lead.
 */
export function useCreateApplication() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (leadId: string) => applicationApi.create(leadId),
    onSuccess: (application) => {
      queryClient.setQueryData(['application', application.lead_id], application)
      queryClient.invalidateQueries({ queryKey: ['lead', application.lead_id] })
    },
  })
}

/**
 * Hook for updating an application step.
 */
export function useUpdateApplicationStep() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      applicationId,
      step,
      data,
    }: {
      applicationId: string
      step: number
      data: Record<string, unknown>
    }) => applicationApi.updateStep(applicationId, step, data),
    onSuccess: (application) => {
      queryClient.setQueryData(['application', application.lead_id], application)
      queryClient.invalidateQueries({ queryKey: ['lead', application.lead_id] })
      queryClient.invalidateQueries({ queryKey: ['leads'] })
    },
  })
}

/**
 * Hook for finalizing an application (ready for eSign).
 */
export function useFinalizeApplication() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (applicationId: string) => applicationApi.finalize(applicationId),
    onSuccess: (application) => {
      queryClient.setQueryData(['application', application.lead_id], application)
      queryClient.invalidateQueries({ queryKey: ['lead', application.lead_id] })
    },
  })
}

export function useApplicationBoardingReadiness(applicationId: string | undefined) {
  return useQuery({
    queryKey: ['application', applicationId, 'boarding-readiness'],
    queryFn: () => applicationApi.boardingReadiness(applicationId!),
    enabled: !!applicationId,
    staleTime: 30_000,
  })
}

/**
 * Hook for dispatching the application to BoldSign for signature.
 *
 * Returns immediately on 202 Accepted. The Reverb-pushed pipeline stage
 * change (esign_sent) confirms the BoldSign job actually delivered the
 * envelope. Until that arrives, treat the button as "in progress".
 */
export function useSendForSignature() {
  return useMutation({
    mutationFn: (applicationId: string) => applicationApi.sendForSignature(applicationId),
  })
}

export function useResendSignature() {
  return useMutation({
    mutationFn: ({ applicationId, ownerEmail }: { applicationId: string; ownerEmail?: string }) =>
      applicationApi.resendSignature(applicationId, ownerEmail),
  })
}

export function useVoidSignature() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ applicationId, reason }: { applicationId: string; reason?: string }) =>
      applicationApi.voidSignature(applicationId, reason),
    onSuccess: (result) => {
      queryClient.setQueryData(['application', result.application.lead_id], result.application)
      queryClient.invalidateQueries({ queryKey: ['lead', result.application.lead_id] })
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      queryClient.invalidateQueries({ queryKey: ['pipeline'] })
    },
  })
}

export function useSubmitWithUploadedDocs() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ applicationId, note }: { applicationId: string; note?: string }) =>
      applicationApi.submitWithUploadedDocs(applicationId, note),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['application'] })
      queryClient.invalidateQueries({ queryKey: ['lead', result.lead_id] })
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      queryClient.invalidateQueries({ queryKey: ['pipeline'] })
    },
  })
}

/**
 * TurboApp wizard steps.
 */
export const APPLICATION_STEPS = {
  1: {
    name: 'Merchant Information',
    description: 'Business name, address, and contact details',
  },
  2: {
    name: 'Business Profile',
    description: 'Business type, volume, and transaction patterns',
  },
  3: {
    name: 'Banking Information',
    description: 'Bank account for settlement',
  },
  4: {
    name: 'Pricing & Rates',
    description: 'Processing rates and fees',
  },
  5: {
    name: 'Beneficial Ownership',
    description: 'Owner and control person information',
  },
  6: {
    name: 'Equipment & Services',
    description: 'Terminal and service selections',
  },
  7: {
    name: 'Review & Submit',
    description: 'Review application and submit for signature',
  },
} as const

/**
 * Check if a specific step is complete based on bitmask.
 */
export function isStepComplete(completedSteps: number, step: number): boolean {
  return (completedSteps & (1 << (step - 1))) !== 0
}

/**
 * Get array of completed step numbers.
 */
export function getCompletedSteps(completedSteps: number): number[] {
  const steps: number[] = []
  for (let i = 1; i <= 7; i++) {
    if (isStepComplete(completedSteps, i)) {
      steps.push(i)
    }
  }
  return steps
}

/**
 * Calculate completion percentage.
 */
export function getCompletionPercentage(completedSteps: number): number {
  const completed = getCompletedSteps(completedSteps).length
  return Math.round((completed / 7) * 100)
}

export type { Application, ApiError }
